import * as vp from 'vopay';
import { FunctionComponent, useEffect, useState } from 'react';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { useNavMenu } from '../../../providers/NavMenu.provider';
import { useClientData } from '../../../providers/ClientData.provider';
import useFileDownload from '../../../hooks/useFiledDownload';
import useFetchPaasTransactions from '../../../hooks/useApi/useFetchPaasTransactions';
import ReconciliationForm from './ReconciliationForm';
import ElinxConnect from '../../ElinxConnect/ElinxConnect';
import styles from './Reconciliation.module.scss';

const { Box, Table, Title, Button, Pagination } = vp;

type PaasTransactionsListListType = {
  [key: number]: vp.PaasTransactionBase;
};

type FC = FunctionComponent;

const ReconciliationContainer: FC = () => {
  const {
    downloadPdfTable,
    downloadCsvFile,
    downloadJson,
    downloadXls,
    downloadDoc,
    downloadTxt,
    downloadXml,
    printFile,
  } = useFileDownload();
  const history = useHistory();
  const { response } = useClientData();
  const { getModuleTitle } = useNavMenu();
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [isExportingCSV, setIsExportingCSV] = useState(false);
  const [isExportingXLS, setIsExportingXLS] = useState(false);
  const [isExportingDOC, setIsExportingDOC] = useState(false);
  const [isExportingText, setIsExportingText] = useState(false);
  const [isExportingJSON, setIsExportingJSON] = useState(false);
  const [isExportingXML, setIsExportingXML] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [hasNewBankAccount, setHasNewBankAccount] = useState(false);
  const timeDifference = moment().subtract(30, 'day').format('YYYY-MM-DD');
  const currentDateTime = moment().format('YYYY-MM-DD');
  const [filteredLists, setFilteredLists] = useState<PaasTransactionsListListType>({});
  const [filteredExportTable, setFilteredExportTable] = useState<PaasTransactionsListListType>({});
  const [paasTransactionsResponse, fetchPaasTransactions] = useFetchPaasTransactions({});
  const [isShowElinx, setIsShowElinx] = useState(false);
  const transactionsPeriod = {
    FromDate: startDate || timeDifference,
    ToDate: endDate || currentDateTime,
  };
  const transactionsData = {
    Transactions: filteredExportTable,
  };
  const [hasInvalidEndDate, setHasInvalidEndDate] = useState(false);
  const [shouldShowEndDateError, setShouldShowEndDateError] = useState(false);

  // pagination
  const [currentPage, setCurrentPage] = useState(0);
  const PER_PAGE = 20;
  const offset = currentPage * PER_PAGE;
  const pageCount = Math.ceil(Object.values(filteredLists || {}).length / PER_PAGE);

  const elinxConnectSettings: vp.EmbedUrlRequest = {
    RedirectURL: 'https://vopay.com',
    Country: response.Country,
    PaymentSelectionMethod: 'bank',
    ClientControlled: true,
    AccountSelectionMethod: 'any',
    RedirectMethod: 'JavascriptMessage',
    WithTransactions: true,
    ForReconciliation: true,
  };

  const onAddPaymentMethod = () => {
    setIsShowElinx(true);
  };
  const onClosePaymentMethod = () => {
    setIsShowElinx(false);
  };
  const onConnectPaymentMethod = () => {
    setIsShowElinx(false);
    setHasNewBankAccount(true);
  };

  // export table
  useEffect(() => {
    if (isExportingPDF) {
      downloadPdfTable('export-pass-transaction-history', 'transactions');
    }
    if (isExportingCSV) {
      downloadCsvFile('export-pass-transaction-history', 'transactions');
    }
    if (isExportingXLS) {
      downloadXls('export-pass-transaction-history', 'transactions');
    }
    if (isExportingDOC) {
      downloadDoc('export-pass-transaction-history', 'transactions');
    }
    if (isExportingText) {
      downloadTxt('export-pass-transaction-history', 'transactions');
    }
    if (isExportingJSON) {
      downloadJson(transactionsData, 'transactions');
    }
    if (isExportingXML) {
      downloadXml('export-pass-transaction-history', 'transactions');
    }
    if (isPrinting) {
      printFile('export-pass-transaction-history');
    }
    setIsExportingPDF(false);
    setIsExportingCSV(false);
    setIsExportingXLS(false);
    setIsExportingDOC(false);
    setIsExportingText(false);
    setIsExportingJSON(false);
    setIsExportingXML(false);
    setIsPrinting(false);
  }, [
    isExportingPDF,
    isExportingCSV,
    isExportingXLS,
    isExportingDOC,
    isExportingText,
    isExportingJSON,
    isExportingXML,
    isPrinting,
  ]);

  useEffect(() => {
    if (!hasInvalidEndDate) {
      fetchPaasTransactions({
        PortalUser: response?.LoginUserName || '',
      });
      setHasNewBankAccount(false);
    } else {
      setShouldShowEndDateError(true);
    }
  }, [hasNewBankAccount]);

  const handleTimeRangeChange = (timeRange: [vp.Timestamp, vp.Timestamp] | undefined) => {
    setStartDate(moment(timeRange?.[0]).format('YYYY-MM-DD'));
    setEndDate(moment(timeRange?.[1]).format('YYYY-MM-DD'));
  };

  const handlePageClick = ({ selected: selectedPage }: { [idx: string]: number }) => {
    setCurrentPage(selectedPage);
  };

  useEffect(() => {
    if (startDate > endDate) {
      setHasInvalidEndDate(true);
      setShouldShowEndDateError(true);
    } else {
      setHasInvalidEndDate(false);
    }
  }, [endDate]);

  useEffect(() => {
    setFilteredLists(
      Object.values(paasTransactionsResponse.Transactions || {}).filter(
        (data) =>
          data.TransactionDate >= transactionsPeriod.FromDate && data.TransactionDate <= transactionsPeriod.ToDate,
      ),
    );
    setFilteredExportTable(
      Object.values(paasTransactionsResponse.Transactions || {}).filter(
        (data) =>
          data.TransactionDate >= transactionsPeriod.FromDate && data.TransactionDate <= transactionsPeriod.ToDate,
      ),
    );
  }, [paasTransactionsResponse.Transactions]);

  useEffect(() => {
    setFilteredLists(
      Object.values(paasTransactionsResponse.Transactions || {}).filter(
        (data: vp.PaasTransactionBase) =>
          ((startDate === '' && endDate === '') ||
            (data.TransactionDate >= transactionsPeriod.FromDate &&
              data.TransactionDate <= transactionsPeriod.ToDate)) &&
          (searchTerm === '' || data.Description.toLocaleLowerCase().includes(searchTerm.toLowerCase())),
      ),
    );
    setFilteredExportTable(
      Object.values(paasTransactionsResponse.Transactions || {}).filter(
        (data: vp.PaasTransactionBase) =>
          ((startDate === '' && endDate === '') ||
            (data.TransactionDate >= transactionsPeriod.FromDate &&
              data.TransactionDate <= transactionsPeriod.ToDate)) &&
          (searchTerm === '' || data.Description.toLocaleLowerCase().includes(searchTerm.toLowerCase())),
      ),
    );
    setCurrentPage(0);
  }, [startDate, endDate, searchTerm, pageCount]);

  const tableProps: vp.TableProps = {
    name: 'pass-transaction-history',
    columns: {
      date: { header: 'Date', align: 'left' },
      credit: { header: 'Credit', align: 'left' },
      debit: { header: 'Debit', align: 'left' },
      balance: { header: 'Balance', align: 'left' },
      description: { header: 'Description', align: 'left' },
    },
    rows:
      Object.values(filteredLists || {})
        .slice(offset, offset + PER_PAGE)
        .map((transaction, index) => ({
          date: transaction.TransactionDate || '',
          credit: `$${transaction.Credit === null ? `0` : String(transaction.Credit.toLocaleString())}` || '',
          debit: `$${transaction.Debit === null ? `0` : String(transaction.Debit.toLocaleString())}` || '',
          balance: `$${transaction.Balance === null ? `0` : String(transaction.Balance.toLocaleString())}` || '',
          description: getDescriptionBtn(
            transaction.Description,
            transaction.Credit || 0,
            transaction.Debit || 0,
            index,
          ),
        })) || [],
  };

  const exportTableProps: vp.TableProps = {
    id: 'export-pass-transaction-history',
    name: 'export-pass-transaction-history',
    columns: {
      date: { header: 'Date', align: 'left' },
      credit: { header: 'Credit', align: 'left' },
      debit: { header: 'Debit', align: 'left' },
      balance: { header: 'Balance', align: 'left' },
      description: { header: 'Description', align: 'left' },
    },
    rows:
      Object.values(filteredExportTable || {}).map((transaction, index) => ({
        date: transaction.TransactionDate || '',
        credit: `$${transaction.Credit === null ? `0` : String(transaction.Credit.toLocaleString())}` || '',
        debit: `$${transaction.Debit === null ? `0` : String(transaction.Debit.toLocaleString())}` || '',
        balance: `$${transaction.Balance === null ? `0` : String(transaction.Balance.toLocaleString())}` || '',
        description: getDescriptionBtn(transaction.Description, transaction.Credit || 0, transaction.Debit || 0, index),
      })) || [],
  };

  return (
    <>
      {isShowElinx && (
        <ElinxConnect
          setting={elinxConnectSettings}
          onSuccess={onConnectPaymentMethod}
          onClose={onClosePaymentMethod}
        />
      )}
      <Box mb="md" jc="sb" ai="center">
        <Box mb="md" jc="sb">
          <Title title={getModuleTitle()} />
        </Box>
        <Button id="sync-bank-account" label="Sync Bank Account" onClick={onAddPaymentMethod} />
      </Box>
      <ReconciliationForm
        searchTerm={searchTerm}
        hasInvalidEndDate={hasInvalidEndDate}
        shouldShowEndDateError={shouldShowEndDateError}
        setShouldShowEndDateError={setShouldShowEndDateError}
        handlePDF={setIsExportingPDF}
        handleCSV={setIsExportingCSV}
        handleXLS={setIsExportingXLS}
        handleDOC={setIsExportingDOC}
        handleText={setIsExportingText}
        handleJSON={setIsExportingJSON}
        handleXML={setIsExportingXML}
        handlePrinting={setIsPrinting}
        handleTimeRangeChange={handleTimeRangeChange}
        handleSearchTermChange={setSearchTerm}
      />
      <Box mt="lg" flx="col">
        <div className={styles.tableContainer} id="transactionTable">
          <Table {...tableProps} />
        </div>
        <div className={styles.tableContainer} style={{ display: 'none' }} id="exportTransactionTable">
          <Table {...exportTableProps} />
        </div>
      </Box>
      <Box width="100%" jc="center" mt="lg">
        {pageCount > 1 && (
          <Pagination pageCount={pageCount} forcePageNumber={currentPage} handlePageClick={handlePageClick} />
        )}
      </Box>
    </>
  );

  function getDescriptionBtn(desc: string, credit: number, debit: number, index: number) {
    if (desc.includes('INTERAC E-TRANSFER BULK SEND') || desc.includes('DEFT SETTLEMENT')) {
      const interacBatchID = desc.split(' ')[5].slice(12);
      const descSplit = desc.split(' ');
      const description = descSplit?.slice(0, descSplit.length - 1).join(' ');
      const label = descSplit[descSplit.length - 1];

      return (
        <>
          {description}{' '}
          <Button
            id={`reconciliation-detail-${index}`}
            label={label}
            color="primary"
            noPadding
            noShadow
            background="transparent"
            textColor="#607AB7"
            textSize={1}
            onClick={() => {
              desc.includes('INTERAC E-TRANSFER BULK SEND')
                ? history.push({
                    pathname: `/reconciliation/details/${interacBatchID}`,
                    state: {
                      id: interacBatchID,
                      service: 'interac',
                      paymentType: credit > 0 ? 'credit' : 'debit',
                      amount: credit > 0 ? credit : debit,
                    },
                  })
                : desc.includes('DEFT SETTLEMENT')
                  ? history.push({
                      pathname: `/reconciliation/details/${label}`,
                      state: {
                        id: label,
                        service: 'eft',
                        paymentType: credit > 0 ? 'credit' : 'debit',
                        amount: credit > 0 ? credit : debit,
                      },
                    })
                  : '';
            }}
          />
        </>
      );
    }

    return desc;
  }
};

export default ReconciliationContainer;
